// Generated by Claude Code
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FormSettingsEditor } from '#components/domain/operations/settings/form-settings-editor.tsx'
import { ProcedureTagsSettings } from '#components/domain/operations/work-orders/procedures/procedure-tags-settings.tsx'
import { Tabs } from '#components/primitives/components/tabs.tsx'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { readGatePairs } from '#core/entities/auth/roles/permission-gate-helpers.ts'
import { useUserState } from '#core/state/UserState.ts'
import { useQuickSettingsReturn } from '#hooks/use-quick-settings-return.ts'

export const Route = createFileRoute('/_appSettings/$companyId/settings/procedures')({
  component: RouteComponent,
})

/**
 * Procedure Details field keys in the order they appear in the New/Edit Procedure form.
 * Keys match the backend procedure form-settings field names (Title + Description are
 * scalar create-schema fields; asset_id/tags/work_type/teams are the virtual Details
 * scoping slots), so the editor's overrides line up with what the drawer enforces.
 */
const PROCEDURE_FIELD_ORDER = ['title', 'description', 'asset_id', 'tags', 'work_type', 'teams'] as const

// Title is always present in the form and cannot be hidden or made optional.
const PROCEDURE_ALWAYS_VISIBLE = ['title']
const PROCEDURE_ALWAYS_REQUIRED = ['title']

type ProceduresSettingsTab = 'form' | 'tags'

/** Settings page for Procedures: the New/Edit form field configuration and the tag catalog. */
function RouteComponent() {
  const { t } = useTranslation('settings')
  const { t: tGeneral } = useTranslation('general')
  const [activeTab, setActiveTab] = useState<ProceduresSettingsTab>('form')
  const userState = useUserState()
  // Quick settings → Advanced → X round-trip, via the shared history-state hook.
  const { showClose, handleClose } = useQuickSettingsReturn()

  // Per-tab permission gating: form settings need the procedures write bit. The step-tag catalog is
  // shared — a procedures read grant opens its tab; the work-order slot enables editing inside it.
  const canEditForm = userState.hasPermission(PermissionService.PROCEDURES, PermissionAction.FORM_SETTINGS)
  const canReadStepTags = userState.hasAnyPermission(readGatePairs(PermissionService.PROCEDURES))

  const tabs = [
    canEditForm && { key: 'form' as const, label: t('proceduresSettings.tabForm') },
    canReadStepTags && { key: 'tags' as const, label: t('shared.fields.tags') },
  ].filter(Boolean) as { key: ProceduresSettingsTab; label: string }[]

  // Fall back to the first permitted tab when the active one is hidden (e.g. a deep link).
  const effectiveTab = tabs.some((tb) => tb.key === activeTab) ? activeTab : tabs[0]?.key

  const fieldLabels: Record<string, string> = {
    title: t('shared.fields.title'),
    description: t('shared.fields.description'),
    asset_id: t('proceduresSettings.formFieldLabels.asset'),
    tags: t('shared.fields.tags'),
    work_type: t('shared.fields.workType'),
    teams: t('shared.fields.teams'),
  }

  return (
    <div className="p-4 overflow-y-auto h-100">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="h4 fw-bold mb-1">{t('proceduresSettings.title')}</h1>
          <p className="text-secondary mb-0">{t('proceduresSettings.description')}</p>
        </div>
        {showClose && (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-outline-secondary border-0"
            onClick={handleClose}
            aria-label={t('shared.actions.close')}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {tabs.length === 0 ? (
        <div className="text-secondary">
          <h2 className="h6 fw-bold mb-1">{tGeneral('notAuthorized.title')}</h2>
          <p className="mb-0">{tGeneral('notAuthorized.message')}</p>
        </div>
      ) : (
        <>
          <Tabs
            tabs={tabs}
            activeKey={effectiveTab}
            onChange={(key) => setActiveTab(key as ProceduresSettingsTab)}
            variant="underline"
            size="sm"
            className="mb-4"
          />

          {/* Form settings tab */}
          {effectiveTab === 'form' && (
            <FormSettingsEditor
              entityType="procedure"
              fieldLabels={fieldLabels}
              orderedFields={[...PROCEDURE_FIELD_ORDER]}
              alwaysVisibleFields={PROCEDURE_ALWAYS_VISIBLE}
              alwaysRequiredFields={PROCEDURE_ALWAYS_REQUIRED}
              canEdit={canEditForm}
              // Procedures have no custom properties — hide the custom-fields subsection entirely.
              showCustomFields={false}
            />
          )}

          {/* Tags tab */}
          {effectiveTab === 'tags' && (
            <div>
              <p className="text-secondary mb-4">{t('proceduresSettings.tagsDescription')}</p>
              <ProcedureTagsSettings />
            </div>
          )}
        </>
      )}
    </div>
  )
}
